import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check, UserPlus, X } from 'lucide-react';
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from '@/lib/assigneeDisplay';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useAssigneeSuggestions } from '@/hooks/useAssigneeSuggestions';

interface SingleAssigneePickerProps {
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  popoverPortalContainer?: HTMLElement | null;
  /** Размер чипа: md (по умолчанию) или sm для компактных карточек. */
  size?: 'sm' | 'md';
}

/**
 * Пикер с автоподсказками и одним выбранным значением. Поддерживает свободный ввод
 * («произвольно»), визуально различает сматченных/несматченных пользователей и
 * позволяет очистить выбор крестиком.
 */
export function SingleAssigneePicker({
  value,
  onChange,
  readOnly = false,
  placeholder = 'Назначить исполнителя',
  popoverPortalContainer,
  size = 'md',
}: SingleAssigneePickerProps) {
  const { assigneeMatches } = useNotifications();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const items = useAssigneeSuggestions(open, query);

  const raw = value.trim();
  const match = raw ? assigneeMatches.get(normalizeAssigneeKey(raw)) ?? null : null;
  const label = raw ? preferredAssigneeLabel(raw, match) : '';
  const tooltip = match
    ? match.isRegistered
      ? `${match.login} — зарегистрирован, уведомление придёт`
      : `${match.login} — приглашён, но ещё не зарегистрировался`
    : raw
      ? 'Не найден среди пользователей — уведомление не будет отправлено'
      : '';

  const assign = (next: string) => {
    const t = next.trim();
    if (!t) {
      onChange('');
    } else {
      onChange(t);
    }
    setQuery('');
    setOpen(false);
  };

  const clear = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    onChange('');
  };

  const selectedKey = raw ? normalizeAssigneeKey(raw) : '';

  const showAddRawButton =
    query.trim().length > 0 &&
    normalizeAssigneeKey(query) !== selectedKey &&
    !items.some(
      (it) =>
        normalizeAssigneeKey(it.login) === normalizeAssigneeKey(query) ||
        (it.displayName && normalizeAssigneeKey(it.displayName) === normalizeAssigneeKey(query)),
    );

  const chipSize = size === 'sm' ? 'text-[11px]' : 'text-xs';
  const iconSize = size === 'sm' ? 'size-4' : 'size-5';
  const iconTextSize = size === 'sm' ? 'text-[9px]' : 'text-[10px]';

  const trigger = raw ? (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-1.5 py-0.5 font-medium leading-none transition-colors',
            chipSize,
            match
              ? 'border-transparent text-white'
              : 'border-dashed border-border bg-muted/40 text-muted-foreground',
            readOnly ? 'cursor-default' : 'cursor-pointer',
          )}
          style={match ? { backgroundColor: colorForAssignee(normalizeAssigneeKey(match.login)) } : undefined}
          disabled={readOnly}
        >
          <span
            aria-hidden
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
              iconSize,
              iconTextSize,
              match ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground',
            )}
          >
            {initialsFor(match?.displayName || match?.login || raw)}
          </span>
          <span className="min-w-0 max-w-[14ch] truncate">{label}</span>
          {!readOnly ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              className={cn(
                'ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full transition-colors',
                match ? 'hover:bg-white/25' : 'hover:bg-red-100 hover:text-red-600',
              )}
              aria-label="Убрать исполнителя"
            >
              <X size={10} strokeWidth={2.5} />
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[22rem] text-xs">
        <div className="space-y-0.5">
          <div className="font-semibold">{label}</div>
          {tooltip ? <div className="text-muted-foreground">{tooltip}</div> : null}
          {match?.login && match.login !== label ? (
            <div className="text-muted-foreground">{match.login}</div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  ) : (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)]',
        chipSize,
      )}
      disabled={readOnly}
    >
      <UserPlus size={12} strokeWidth={2.2} />
      {placeholder}
    </button>
  );

  if (readOnly) {
    return <div className="flex min-w-0 items-center">{trigger}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        container={popoverPortalContainer}
        className="z-[100] w-[min(24rem,calc(100vw-1.5rem))] p-0 shadow-lg"
      >
        <Command shouldFilter={false} className="h-auto max-h-[min(22rem,calc(100dvh-9rem))] min-h-0 flex-col rounded-md">
          <CommandInput
            placeholder="Найти по ФИО или e-mail…"
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                assign(query);
                e.preventDefault();
              }
            }}
          />
          <CommandList className="max-h-[min(18rem,calc(100dvh-11rem))] min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
            {items.length === 0 && !showAddRawButton ? (
              <CommandEmpty>Никого не найдено</CommandEmpty>
            ) : null}
            {items.length > 0 ? (
              <CommandGroup heading="Пользователи и приглашения">
                {items.map((it) => {
                  const key = normalizeAssigneeKey(it.login);
                  const selected = key === selectedKey;
                  const itemLabel = it.displayName?.trim() || it.login;
                  return (
                    <CommandItem
                      key={key}
                      value={`${it.login} ${it.displayName ?? ''}`}
                      onSelect={() => {
                        assign(it.displayName?.trim() || it.login);
                      }}
                      className="cursor-pointer items-center gap-2 py-2"
                    >
                      <span
                        aria-hidden
                        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                        style={{ backgroundColor: colorForAssignee(key) }}
                      >
                        {initialsFor(itemLabel)}
                      </span>
                      <span className="min-w-0 flex-1 leading-tight">
                        <span className="block truncate text-sm">{itemLabel}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {it.login}
                          {' · '}
                          {it.isRegistered ? (
                            <span className="text-emerald-600">зарегистрирован</span>
                          ) : (
                            <span className="text-amber-600">приглашён</span>
                          )}
                        </span>
                      </span>
                      {selected ? <Check size={14} className="text-muted-foreground" /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {showAddRawButton ? (
              <CommandGroup heading="Назначить произвольно">
                <CommandItem
                  value={`__raw__${query}`}
                  onSelect={() => assign(query)}
                  className="cursor-pointer items-center gap-2 py-2"
                >
                  <UserPlus size={14} className="text-muted-foreground" />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-sm">«{query.trim()}»</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      без связи с пользователем — уведомление не уйдёт
                    </span>
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {raw ? (
              <CommandGroup heading="Текущий исполнитель">
                <CommandItem
                  value="__clear__"
                  onSelect={() => assign('')}
                  className="cursor-pointer items-center gap-2 py-2 text-muted-foreground"
                >
                  <X size={14} />
                  <span>Убрать исполнителя</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
